import {
  faCheckCircle,
  faCreditCard,
  faInfoCircle,
  faQuestionCircle,
  faSave,
  faXmarkCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { FormikHelpers, FormikProps, useFormikContext } from 'formik';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import ModalInner from './modal-inner';
import type { ButtonProps } from '../button/button';
import { Form, type FormProps, type FormValues } from '../form/form';
import {
  Box,
  Dialog,
  DialogContent,
  DialogProps,
  IconButton,
  PaperProps,
} from '@mui/material';
import { Close, Download } from '@mui/icons-material';

export type TField =
  | string
  | number
  | boolean
  | Date
  | undefined
  | object
  | File;

export interface IModalProperties {
  headerClassNames: string[];
  icon?: FontAwesomeIconProps['icon'];
  muiIcon?: ReactNode;
  isLoading: boolean;
  buttons: ButtonProps[];
  onClose?: () => void;
  hideHeader?: boolean;
}

export interface IModalFooterProps<TForm = Record<string, unknown>> {
  formikProps?: FormikProps<TForm>;
  modalProps: IModalProperties;
}

interface IModalBase {
  type: 'info' | 'ask' | 'save' | 'payment' | 'readOnly';
  title: string | ReactNode;
  icon?: FontAwesomeIconProps['icon'];
  iconClassName?: string;
  muiIcon?: ReactNode;
  fullscreen?: string | true;
  isLoading?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  error?: string;
  // @deprecated
  backdrop?: string | boolean;
  disableBackdropClick?: boolean;
  backdropClassName?: string;
  centered?: boolean;
  onCancel?: () => void;
  dialogProps?: Partial<DialogProps>;
  hideHeader?: boolean;
}

interface IModalInfo {
  type: 'info';
  okText?: string;
  okIcon?: FontAwesomeIconProps['icon'];
  body: ReactNode | ((ModalProps: IModalProperties) => ReactNode);
  footer?: ReactNode;
}

interface IModalAsk {
  type: 'ask';
  variant?: string;
  yesDisabled?: boolean;
  yesText?: string;
  yesIcon?: FontAwesomeIconProps['icon'];
  noText?: string;
  noIcon?: FontAwesomeIconProps['icon'];
  body: ReactNode;
  footer?:
    | ReactNode
    | (({ formikProps, modalProps }: IModalFooterProps) => ReactNode);
  onConfirm?: (() => void) | (() => Promise<void>);
}

interface IModalSave<TForm extends FormValues<TForm>> {
  type: 'save';
  saveText?: string;
  saveIcon?: FontAwesomeIconProps['icon'];
  cancelText?: string;
  cancelIcon?: FontAwesomeIconProps['icon'];
  initialValues: FormProps<TForm>['initialValues'];
  body: ReactNode | ((formikProps: FormikProps<TForm>) => ReactNode);
  footer?:
    | ReactNode
    | (({ formikProps, modalProps }: IModalFooterProps<TForm>) => ReactNode);
  validate?: FormProps<TForm>['validate'];
  onSubmit?:
    | ((values: TForm, formikHelpers: FormikHelpers<TForm>) => void)
    | ((values: TForm, formikHelpers: FormikHelpers<TForm>) => Promise<void>);
}

interface IModalReadOnly<TForm extends FormValues<TForm>> {
  type: 'readOnly';
  okText?: string;
  okIcon?: FontAwesomeIconProps['icon'];
  initialValues: FormProps<TForm>['initialValues'];
  body: ReactNode | ((formikProps: FormikProps<TForm>) => ReactNode);
  footer?:
    | ReactNode
    | (({ formikProps, modalProps }: IModalFooterProps<TForm>) => ReactNode);
}

interface IModalPayment {
  type: 'payment';
  body: ReactNode;
  footer: null;
}

/** Displays an image or PDF at full size with no chrome other than a close button. */
export interface IModalImagePreview {
  type: 'imagePreview';
  /** The URL of the image or PDF to display. */
  src: string;
  /** Accessible alt text for the image. */
  alt?: string;
  /** Optional mime type — used to decide whether to render an iframe (e.g. 'application/pdf'). */
  mimeType?: string;
}

export type IModal<
  TForm extends FormValues<TForm> = Partial<Record<string, TField>>,
> =
  | (IModalBase &
      (
        | IModalInfo
        | IModalAsk
        | IModalSave<TForm>
        | IModalPayment
        | IModalReadOnly<TForm>
      ))
  | IModalImagePreview;

export interface IModalRendererProps<TForm extends FormValues<TForm>> {
  modal: IModal<TForm>;
  onClose?: () => void;
}

export default function ModalRenderer<TForm extends FormValues<TForm>>({
  modal,
  onClose,
}: IModalRendererProps<TForm>) {
  const [show, setShow] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // Image preview modals render their own minimal UI — bypass all standard chrome.
  if (modal.type === 'imagePreview') {
    const handleClose = () => {
      setShow(false);
      // Notify the provider so it clears the modal from context. This ensures
      // the next preview() call triggers a fresh mount with show=true.
      onClose?.();
    };
    const isPdf = modal.mimeType === 'application/pdf';
    return (
      <Dialog open={show} onClose={handleClose} maxWidth="lg">
        <DialogContent sx={{ p: 0, position: 'relative', lineHeight: 0 }}>
          {isPdf ? (
            /* PDF: dedicated toolbar above the iframe so buttons don't overlap native PDF controls */
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  bgcolor: 'grey.900',
                }}
              >
                <IconButton
                  size="small"
                  component="a"
                  href={modal.src}
                  download={modal.alt ?? true}
                  aria-label="Download file"
                  sx={{
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  <Download fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleClose}
                  aria-label="Close preview"
                  sx={{
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
              <Box
                component="iframe"
                src={modal.src}
                title={modal.alt ?? 'PDF Preview'}
                sx={{
                  display: 'block',
                  width: '80vw',
                  height: '85vh',
                  border: 'none',
                }}
              />
            </Box>
          ) : (
            /* Image: floating buttons over the image */
            <>
              <IconButton
                size="small"
                component="a"
                href={modal.src}
                download={modal.alt ?? true}
                aria-label="Download file"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 48,
                  bgcolor: 'rgba(0,0,0,0.45)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                  zIndex: 1,
                }}
              >
                <Download fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleClose}
                aria-label="Close preview"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0,0,0,0.45)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                  zIndex: 1,
                }}
              >
                <Close fontSize="small" />
              </IconButton>
              <Box
                component="img"
                src={modal.src}
                alt={modal.alt ?? 'Preview'}
                sx={{
                  display: 'block',
                  maxWidth: '90vw',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  useEffect(() => {
    setShow(!!modal);
  }, [modal]);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    try {
      if (modal?.type === 'ask' && modal?.onConfirm) {
        await modal.onConfirm();
      }
      setShow(false);
    } catch (error) {
      modal.error = (error as Error).message;
    } finally {
      setIsLoading(false);
    }
  }, [modal]);

  const handleSubmit = useCallback(
    async (values: TForm, formikHelpers: FormikHelpers<TForm>) => {
      setIsLoading(true);
      if (modal?.type === 'save' && modal?.onSubmit) {
        try {
          await modal.onSubmit(values, formikHelpers);
          setShow(false);
        } catch (error) {
          modal.error = (error as Error).message;
        } finally {
          setIsLoading(false);
        }
      }
    },
    [modal],
  );

  const handleClose = useCallback(() => {
    setShow(false);
    if (modal?.onCancel) {
      modal.onCancel();
    }
  }, [modal]);

  const modalProps: IModalProperties = useMemo(() => {
    switch (modal.type) {
      case 'info':
        return {
          headerClassNames: ['bg-body-secondary'],
          icon: faInfoCircle,
          onClose: handleClose,
          isLoading,
          hideHeader: modal.hideHeader,
          buttons: [
            {
              content: modal.okText || 'Ok',
              icon: modal.okIcon || faCheckCircle,
              variant: 'primary',
              onClick: handleClose,
            },
          ],
        };
      case 'ask':
        return {
          headerClassNames: [`bg-${modal.variant || 'body-secondary'}`],
          icon: faQuestionCircle,
          iconClassName: modal.iconClassName || 'text-warning',
          onClose: handleClose,
          isLoading,
          hideHeader: modal.hideHeader,
          buttons: [
            {
              content: modal.noText || 'Cancel',
              icon: modal.noIcon || faXmarkCircle,
              variant: 'ghost',
              onClick: handleClose,
            },
            {
              type: 'submit',
              content: modal.yesText || 'Yes',
              icon: modal.yesIcon || faCheckCircle,
              variant: 'primary',
              onClick: handleConfirm,
              disabled: modal.yesDisabled,
              isLoading,
            },
          ],
        };
      case 'payment':
        return {
          headerClassNames: ['bg-body-secondary'],
          icon: faCreditCard,
          onClose: handleClose,
          isLoading,
          hideHeader: modal.hideHeader,
          buttons: [],
        };
      case 'save':
        return {
          headerClassNames: ['bg-body-secondary'],
          icon: faSave,
          onClose: handleClose,
          isLoading,
          hideHeader: modal.hideHeader,
          buttons: [
            {
              content: modal.cancelText || 'Cancel',
              variant: 'ghost',
              onClick: handleClose,
              disabled: isLoading,
            },
            {
              type: 'submit',
              content: modal.saveText || 'Save',
              icon: modal.saveIcon || faSave,
              variant: 'primary',
              isLoading,
            },
          ],
        };
      case 'readOnly':
        return {
          headerClassNames: ['bg-body-secondary'],
          icon: faSave,
          onClose: handleClose,
          isLoading,
          buttons: [
            {
              content: modal.okText || 'Ok',
              icon: modal.okIcon || faCheckCircle,
              variant: 'primary',
              onClick: handleClose,
            },
          ],
        };
      default:
        return {
          headerClassNames: [],
          icon: faInfoCircle,
          isLoading,
          buttons: [],
        };
    }
  }, [handleClose, handleConfirm, isLoading, modal]);

  // If the Body uses Formik, we need to render it with Formik context
  const ModalBody: React.FC = ['save', 'readOnly'].includes(modal.type)
    ? ((() => {
        const formikProps = useFormikContext<TForm>();
        return (
          <ModalInner
            modal={modal}
            modalProps={modalProps}
            formikProps={formikProps}
          />
        );
      }) as React.FC)
    : () => <ModalInner modal={modal} modalProps={modalProps} />;

  const dialogPaperComponentProps = useMemo(() => {
    switch (modal.type) {
      case 'save':
        return {
          component: Form,
          onSubmit: handleSubmit,
          initialValues: modal.initialValues,
          validate: modal.validate,
        };

      case 'readOnly':
        return {
          component: Form,
          initialValues: modal.initialValues,
        };

      default:
        return {};
    }
  }, [modal.fullscreen, show, handleClose]);

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      fullScreen={!!modal.fullscreen || false}
      maxWidth={modal.size || 'md'}
      fullWidth
      slotProps={{
        paper: dialogPaperComponentProps as PaperProps<
          'form',
          FormikProps<TForm>
        >,
      }}
      {...modal.dialogProps}
    >
      <ModalBody />
    </Dialog>
  );
}
